import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import './Auth.css';

const Auth = ({ onAuthSuccess }) => {
    const [showAuth, setShowAuth] = useState(false);
    const [isLogin, setIsLogin] = useState(true);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const handleGoogleSignIn = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: window.location.origin,
                }
            });
            if (error) throw error;

            if (data?.url) {
                window.location.href = data.url;
            }

            setTimeout(() => {
                setLoading(false);
            }, 5000);
        } catch (err) {
            setError(err.message);
            alert("Auth Error: " + err.message);
            setLoading(false);
        }
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setMessage(null);

        try {
            if (isLogin) {
                const { data, error: signInError } = await supabase.auth.signInWithPassword({
                    email,
                    password,
                });
                if (signInError) throw signInError;
                if (data.session) onAuthSuccess(data.session);
            } else {
                const { error: signUpError } = await supabase.auth.signUp({
                    email,
                    password,
                });
                if (signUpError) throw signUpError;
                setMessage('Check your email for the confirmation link!');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const openAuth = (loginMode) => {
        setIsLogin(loginMode);
        setShowAuth(true);
    };

    if (!showAuth) {
        return (
            <div className="landing-container animated">
                <nav className="landing-nav">
                    <div className="landing-logo">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>MoodSnap</span>
                    </div>
                    <div className="landing-nav-actions">
                        <button className="landing-login-btn" onClick={() => openAuth(true)}>Log In</button>
                        <button className="landing-signup-btn" onClick={() => openAuth(false)}>Sign Up</button>
                    </div>
                </nav>

                <main className="landing-hero">
                    <h1>Track Your Vibe.<br />Find Your Zen.</h1>
                    <p>Your beautiful, AI-powered mental wellness companion. Log your moods, discover deep emotional patterns, and find balance with therapeutic mini-games.</p>
                    <button className="landing-cta-btn" onClick={() => openAuth(false)}>Start Your Journey</button>
                </main>

                <section className="landing-features">
                    <div className="feature-card">
                        <div className="feature-icon">📝</div>
                        <h3>Intelligent Tracking</h3>
                        <p>Log your daily emotional states effortlessly in our meticulously crafted, distraction-free environment.</p>
                    </div>
                    <div className="feature-card">
                        <div className="feature-icon">🧠</div>
                        <h3>AI Pattern Recognition</h3>
                        <p>Our advanced AI anonymously analyzes your entries over time to uncover deep emotional triggers and weekly trends.</p>
                    </div>
                    <div className="feature-card">
                        <div className="feature-icon">🎮</div>
                        <h3>Therapeutic Games</h3>
                        <p>Ground yourself immediately with highly interactive breathing exercises, gratitude amplification, and sand canvases.</p>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="auth-container animated">
            <button className="landing-back-btn" onClick={() => setShowAuth(false)}>
                ← Back to Home
            </button>
            <div className="auth-card glass-panel">
                <div className="auth-header">
                    <div className="auth-logo">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>MoodSnap</span>
                    </div>
                    <h1>{isLogin ? 'Welcome Back' : 'Join MoodSnap'}</h1>
                    <p>{isLogin ? 'Sign in to continue your wellness journey' : 'Start tracking your mood today'}</p>
                </div>

                <div className="auth-social">
                    <button className="google-btn" onClick={handleGoogleSignIn} disabled={loading}>
                        <svg width="18" height="18" viewBox="0 0 18 18">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285f4" />
                            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34a853" />
                            <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#fbbc05" />
                            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962l3.007 2.332C4.672 5.164 6.656 3.58 9 3.58z" fill="#ea4335" />
                        </svg>
                        Continue with Google
                    </button>
                    <div className="auth-divider">
                        <span>OR</span>
                    </div>
                </div>

                <form className="auth-form" onSubmit={handleAuth}>
                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            placeholder="your@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Password</label>
                        <input
                            type="password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    {error && <div className="auth-error">{error}</div>}
                    {message && <div className="auth-success">{message}</div>}

                    <button type="submit" className="auth-submit-btn" disabled={loading}>
                        {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Sign Up')}
                    </button>
                </form>

                <div className="auth-footer">
                    <p>
                        {isLogin ? "Don't have an account?" : "Already have an account?"}
                        <button type="button" className="auth-toggle-btn" onClick={() => setIsLogin(!isLogin)}>
                            {isLogin ? 'Create one' : 'Sign in'}
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Auth;
